import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAlbums, useCreateAlbum, useUpdateAlbum, useDeleteAlbum } from '../hooks/useAlbums'
import { adminApiClient } from '../api/client'
import { getThumbnailUrl } from '../lib/images'
import { Album } from '../types/album'
import { Visibility, requiresShareToken } from '../types/visibility'
import { VisibilitySelector } from '../components/common/VisibilitySelector'
import { VisibilityBadge } from '../components/common/VisibilityBadge'
import { formatDate } from '../utils/formatting'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import {
  Plus,
  Edit2,
  Trash2,
  FolderHeart,
  Image,
  Check,
  X,
  Search,
  Copy,
  Key,
} from 'lucide-react'

export const AlbumManagement = () => {
  const { data: albums, isLoading } = useAlbums(adminApiClient)
  const createAlbum = useCreateAlbum(adminApiClient)
  const deleteAlbum = useDeleteAlbum(adminApiClient)

  const [isCreating, setIsCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [albumToDelete, setAlbumToDelete] = useState<{ id: string; name: string } | null>(null)
  const [formData, setFormData] = useState<{
    name: string
    description: string
    visibility: Visibility
    sharePassword: string
  }>({
    name: '',
    description: '',
    visibility: 'public',
    sharePassword: '',
  })

  const filteredAlbums = albums?.filter(album =>
    album.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    album.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    if (requiresShareToken(formData.visibility) && !formData.sharePassword) {
      // The backend rejects discoverable/private without a password.
      return
    }

    await createAlbum.mutateAsync({
      name: formData.name,
      description: formData.description || undefined,
      visibility: formData.visibility,
      sharePassword: formData.sharePassword || undefined,
    })

    setFormData({ name: '', description: '', visibility: 'public', sharePassword: '' })
    setIsCreating(false)
  }

  const handleDelete = (id: string, name: string) => {
    setAlbumToDelete({ id, name })
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (albumToDelete) {
      deleteAlbum.mutate(albumToDelete.id)
      setDeleteConfirmOpen(false)
      setAlbumToDelete(null)
    }
  }

  return (
    <Layout
      title="Manage Albums"
      actions={
        !isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Create Album
          </button>
        )
      }
    >
      <div className="max-w-4xl mx-auto">
        {/* Create Album Form */}
        {isCreating && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900">Create New Album</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Album Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="e.g., Summer Vacation 2024"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Visibility
                </label>
                <VisibilitySelector
                  value={formData.visibility}
                  onChange={(v) => setFormData({ ...formData, visibility: v })}
                />
                {requiresShareToken(formData.visibility) && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Share Password *
                    </label>
                    <input
                      type="text"
                      value={formData.sharePassword}
                      onChange={(e) => setFormData({ ...formData, sharePassword: e.target.value })}
                      className="w-full md:w-1/2 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Required for this visibility"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Visitors will need this password to unlock the album contents.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  type="submit"
                  disabled={createAlbum.isPending || !formData.name.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {createAlbum.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Album
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false)
                    setFormData({ name: '', description: '', visibility: 'public', sharePassword: '' })
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search albums..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Albums List */}
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-100 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-1/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredAlbums?.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <FolderHeart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No albums found' : 'No albums yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Create your first album to organize your photos'}
            </p>
            {!searchQuery && !isCreating && (
              <button
                onClick={() => setIsCreating(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Create First Album
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAlbums?.map((album) => (
              <div
                key={album.albumId}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                {editingId === album.albumId ? (
                  <AlbumEditForm
                    album={album}
                    onCancel={() => setEditingId(null)}
                    onSaved={() => setEditingId(null)}
                  />
                ) : (
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Album Cover */}
                      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                        {album.coverThumbnailKeys ? (
                          <img
                            src={getThumbnailUrl({ thumbnailKeys: album.coverThumbnailKeys, updatedAt: album.updatedAt }, 'small')}
                            alt={album.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        ) : album.coverImageId ? (
                          <img
                            src={`${import.meta.env.VITE_CDN_URL}/${album.coverImageId}`}
                            alt={album.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        ) : (
                          <FolderHeart className="w-8 h-8 text-gray-400" />
                        )}
                      </div>

                      {/* Album Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{album.name}</h3>
                            {album.description && (
                              <p className="text-gray-500 mt-1 line-clamp-2">{album.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Image className="w-4 h-4" />
                                {album.imageCount || 0} photos
                              </span>
                              <span className="flex items-center gap-1">
                                <VisibilityBadge visibility={album.visibility} className="px-2 py-0.5" />
                              </span>
                              {album.sharePassword && (
                                <span className="flex items-center gap-1">
                                  <Key className="w-4 h-4" />
                                  <code className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{album.sharePassword}</code>
                                </span>
                              )}
                              <span>Created {formatDate(album.createdAt)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/albums/${album.albumId}`}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="View Photos"
                            >
                              <Image className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => setEditingId(album.albumId)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(album.albumId, album.name)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false)
          setAlbumToDelete(null)
        }}
        title="Delete Album"
        message={`Are you sure you want to delete "${albumToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </Layout>
  )
}

interface AlbumEditFormProps {
  album: Album
  onCancel: () => void
  onSaved: () => void
}

const AlbumEditForm = ({ album, onCancel, onSaved }: AlbumEditFormProps) => {
  const [editData, setEditData] = useState<{
    name: string
    description: string
    visibility: Visibility
    sharePassword: string
  }>({
    name: album.name,
    description: album.description || '',
    visibility: album.visibility,
    sharePassword: album.sharePassword || '',
  })
  const [copySuccess, setCopySuccess] = useState(false)
  const updateAlbum = useUpdateAlbum(adminApiClient)

  const shareUrl = `${window.location.origin}/share/${album.albumId}`

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareUrl)
    setCopySuccess(true)
    setTimeout(() => setCopySuccess(false), 2000)
  }

  const handleSave = async () => {
    if (requiresShareToken(editData.visibility) && !editData.sharePassword) {
      // Backend rejects a gating visibility without a sharePassword. Block the
      // save in the UI as well so the user gets a chance to set one first.
      return
    }
    await updateAlbum.mutateAsync({
      id: album.albumId,
      input: {
        name: editData.name,
        description: editData.description || undefined,
        visibility: editData.visibility,
        sharePassword: editData.sharePassword || null,
      },
    })
    onSaved()
  }

  return (
    <div className="p-6 bg-blue-50/50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={editData.name}
          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Album name"
        />
        <input
          type="text"
          value={editData.description}
          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Description"
        />
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
        <VisibilitySelector
          value={editData.visibility}
          onChange={(v) => setEditData({ ...editData, visibility: v })}
        />
      </div>
      {requiresShareToken(editData.visibility) && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Share Password</label>
          <input
            type="text"
            value={editData.sharePassword}
            onChange={(e) => setEditData({ ...editData, sharePassword: e.target.value })}
            className="w-full md:w-1/2 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Required for this visibility"
          />
          <p className="text-xs text-gray-500 mt-1">Anyone with this password can view the album at the share link.</p>
          {editData.sharePassword && (
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Copy className="w-3 h-3" />
                {copySuccess ? 'Copied!' : 'Copy share link'}
              </button>
              <span className="text-xs text-gray-500 truncate">{shareUrl}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 mt-4">
        <button
          onClick={handleSave}
          disabled={updateAlbum.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
        >
          {updateAlbum.isPending ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </div>
  )
}